import ISupplierModel from "@models/ISupplierModel";
import SupplierService from "@services/SupplierService";
import IUserUI from "@models/IUserUI";
import ICatalog from "@models/ICatalog";
import ICategory from "@models/ICategory";
import IRoute from "@models/IRoute";
import IUser from "@models/IUser";
import IWorkflow from "@models/IWorkflow";
import IOrderSearchConfig from "@models/IOrderSearchConfig";
import OrderSearchColEnum from "@models/OrderSearchColEnum";
import OrderItemTypeEnum from "@models/OrderItemTypeEnum";
import SupplierGuidEnum from "@models/SupplierGuidEnum";
import UserTypesEnum from "@models/UserTypesEnum";
import QBOPaymentSyncModeEnum from "@models/QBOPaymentSyncModeEnum";
import AccessTypesEnum from "@models/AccessTypesEnum";
import { FilterValue } from "@models/IFilterPresets";
import { FilterValues, IFilterField, IFilterSection, IFilterSelectOption } from "@models/IFilterConfig";

/**
 * UI-only preset key for Percent Fillable. It is not a queryString param: the Orders screen pulls
 * `includePercentageFillable` out of the buildApiQuery() result and passes it as the separate
 * request argument, exactly as the legacy OrderFilter.performSearch does.
 */
export const PERCENT_FILLABLE_KEY = "percentage_fillable";
export const PERCENT_FILLABLE_API_KEY = "includePercentageFillable";

/**
 * Lookup functions for typeahead-backed filters (guid/id params).
 * `resolve*` turns a stored id back into an option so chips can show the name.
 */
export interface IOrderFilterLookups {
    vendors: (text: string) => Promise<IFilterSelectOption[]>;
    clients: (text: string) => Promise<IFilterSelectOption[]>;
    tags: (text: string) => Promise<IFilterSelectOption[]>;
    resolveVendor?: (guid: string) => Promise<IFilterSelectOption | undefined>;
    resolveClient?: (guid: string) => Promise<IFilterSelectOption | undefined>;
    resolveTag?: (id: string) => Promise<IFilterSelectOption | undefined>;
}

/**
 * Context object with all dynamic data needed to compile the Orders filter options.
 * Fetched once on screen load by the parent and passed to the builder.
 */
export interface IOrderFilterContext {
    supplierModel: ISupplierModel;
    userModel: IUserUI;
    catalogs: ICatalog[];
    users: IUser[];
    orderCategories: ICategory[];
    orderRoutes: IRoute[];
    workflows: IWorkflow[];
    orderStatuses: { id: number; name: string }[];
    lookups: IOrderFilterLookups;
    /** Translate a message id (used for static option labels) */
    formatMessage: (id: string) => string;
    /**
     * Compile the Quotes variant: the Quotes screen is the Orders screen with `is_quote=1`, and the
     * legacy OrderFilter hides the order-lifecycle fields there (status, ship/delivery dates, document
     * status, the shipped/delivered exclusions, ...) and adds the "closed" exclusion instead.
     */
    isQuotes?: boolean;
    /**
     * Compile the Templates variant: the Templates screen is the Orders screen with `is_template=1`, and the
     * legacy OrderFilter hides status, parent order, order type, backorders and the whole Filter Out group there.
     */
    isTemplates?: boolean;
}

/** Baseline Orders filters: what "All Orders", Reset and Clear all return to (the legacy default view). */
export const ORDERS_DEFAULT_FILTERS: FilterValues = {
    filter_shipped: true,
    filter_picked_up: true,
    filter_delivered: true,
    filter_older_three_months: true,
};

/** Baseline Quotes filters: the legacy Quotes reset navigates to `/quotes?filter_closed=true` (hide closed quotes). */
export const QUOTES_DEFAULT_FILTERS: FilterValues = {
    filter_closed: true,
};

/**
 * Baseline Templates filters: none. The legacy Templates reset navigates to a bare `/templates` and hides every
 * exclusion toggle; templates are long-lived, so "All Templates" must not silently drop older ones.
 */
export const TEMPLATES_DEFAULT_FILTERS: FilterValues = {};

const joinComma = (value: FilterValue): unknown => (Array.isArray(value) ? value.join(",") : value);
/** `is_*` flags and has_serial_items are sent as "1" (matches OrdersService.getFilterQuery) */
const asOne = (): unknown => "1";

/**
 * Compile the Orders filter config, injecting dynamic options from user data.
 * Field keys ARE the wire names (apiKey) so presets — including ones migrated from the legacy
 * `orders_filters` bag — store API param names. Visibility gates mirror the legacy OrderFilter.
 */
export function buildOrdersFilterSections(ctx: IOrderFilterContext): IFilterSection[] {
    const { supplierModel, userModel, formatMessage } = ctx;
    const isQuotes = !!ctx.isQuotes;
    const isTemplates = !!ctx.isTemplates;
    const isSupplierUser = !!userModel.isSupplierUser;
    const isPanasonic = supplierModel.guid === SupplierGuidEnum.PANASONIC_CA;
    const isStandardType = supplierModel.order_item_type_id === OrderItemTypeEnum.Standard;
    const integration = supplierModel.Supplier_Integration;
    const orderDetails = (supplierModel.settings?.order_details || []).map((d) => String(d));
    const isAdmin = isSupplierUser && userModel.userType?.id === UserTypesEnum.ADMIN;

    const yesNoOptions: IFilterSelectOption[] = [
        { label: formatMessage("any"), value: "" },
        { label: formatMessage("yes"), value: "true" },
        { label: formatMessage("no"), value: "false" },
    ];

    const vendorLabel =
        supplierModel.vendor_name_override && supplierModel.vendor_name_override !== "null" ? supplierModel.vendor_name_override : "vendor";

    const canFilterByUser =
        isSupplierUser &&
        (userModel.userType?.id === UserTypesEnum.ADMIN ||
            (userModel.userType?.id === UserTypesEnum.SALES && userModel.supplierUserToken?.Access_Type?.id === AccessTypesEnum.FULL));

    // ── Section 1: Quick Filters ──
    const quick: IFilterField[] = [];

    if (isStandardType && isSupplierUser) {
        quick.push({
            key: "vendor",
            label: vendorLabel,
            type: "typeahead",
            apiKey: "vendor",
            lookup: ctx.lookups.vendors,
            resolveOption: ctx.lookups.resolveVendor,
        });
    }

    // Suppliers that rename orders (e.g. Panasonic's "deal") get the renamed label, exactly as the legacy OrderFilter did.
    const orderLabel = isQuotes
        ? "quote"
        : isTemplates
          ? "template"
          : SupplierService.updateStringWithOrderNameOverride("order", supplierModel);

    quick.push({ key: "order", label: orderLabel, type: "text", apiKey: "order" });

    if (isStandardType && !isQuotes && !isTemplates && ctx.orderStatuses.length) {
        quick.push({
            key: "order_state",
            label: "status",
            type: "select",
            apiKey: "order_state",
            options: [
                { label: formatMessage("all"), value: "" },
                ...ctx.orderStatuses.map((s) => ({ label: formatMessage(s.name), value: String(s.id) })),
            ],
        });
    }

    if (!isQuotes && ((supplierModel.has_lead_times && supplierModel.shipping_lead_time) || supplierModel.has_est_ship_date)) {
        quick.push({ key: "ship_date_range_start", label: "ship_date_from", type: "date", apiKey: "ship_date_range_start" });
        quick.push({ key: "ship_date_range_end", label: "ship_date_to", type: "date", apiKey: "ship_date_range_end" });
    }

    if (isStandardType && canFilterByUser) {
        quick.push({
            key: "user",
            label: "user",
            type: "select",
            apiKey: "user",
            options: [{ label: formatMessage("all"), value: "" }, ...ctx.users.map((u) => ({ label: u.name, value: u.guid }))],
        });
    }

    // ── Section 2: Order Identification ──
    const identification: IFilterField[] = [];

    if (!isPanasonic) {
        identification.push({ key: "po_no", label: "po_no", type: "text", apiKey: "po_no" });
    }

    if (!isPanasonic && !isQuotes && !isTemplates) {
        identification.push({
            key: "parent_order",
            label: SupplierService.updateStringWithOrderNameOverride("parent_order", supplierModel),
            type: "text",
            apiKey: "parent_order",
        });
    }

    if (isSupplierUser && (supplierModel.has_client_module || supplierModel.order_item_type_id === OrderItemTypeEnum.Nuts)) {
        identification.push({
            key: "client",
            label: "client",
            type: "typeahead",
            apiKey: "client",
            lookup: ctx.lookups.clients,
            resolveOption: ctx.lookups.resolveClient,
        });
    }

    if (isStandardType && !isQuotes && ctx.catalogs.length > 1) {
        identification.push({
            key: "catalog",
            label: "catalog",
            type: "select",
            apiKey: "catalog",
            options: [{ label: formatMessage("all"), value: "" }, ...ctx.catalogs.map((c) => ({ label: c.name, value: c.guid }))],
        });
    }

    if (isStandardType) {
        identification.push({ key: "description", label: "description", type: "text", apiKey: "description" });
    }

    if (!isPanasonic) {
        identification.push({
            key: "tags",
            label: "tags",
            type: "typeahead",
            multiple: true,
            apiKey: "tags",
            lookup: ctx.lookups.tags,
            resolveOption: ctx.lookups.resolveTag,
            serialize: joinComma,
        });
    }

    if (isSupplierUser && !isQuotes && integration?.has_fishbowl) {
        identification.push({ key: "fishbowl_so_id", label: "fishbowl_so", type: "text", apiKey: "fishbowl_so_id" });
    }

    if (supplierModel.has_third_party_order_ref && !isQuotes) {
        identification.push({
            key: "third_party_order_ref",
            label: "third_party_order_ref",
            type: "text",
            apiKey: "third_party_order_ref",
        });
    }

    if (orderDetails.includes("deceased_last_name") && !isQuotes) {
        identification.push({ key: "deceased_last_name", label: "deceased_last_name", type: "text", apiKey: "deceased_last_name" });
    }

    // ── Section 3: Order Classification ──
    const classification: IFilterField[] = [];

    if (supplierModel.has_noninventory_orders && !isQuotes && !isTemplates) {
        classification.push({
            key: "filter_order_type",
            label: "order_type",
            type: "select",
            apiKey: "filter_order_type",
            options: [
                { label: formatMessage("all"), value: "" },
                { label: formatMessage("inventory"), value: "inventory" },
                { label: formatMessage("noninventory"), value: "noninventory" },
            ],
        });
    }

    if (supplierModel.has_order_categories) {
        classification.push({
            key: "selected_order_categories",
            label: "order_category",
            type: "multiselect",
            apiKey: "selected_order_categories",
            options: ctx.orderCategories.map((c) => ({ label: c.name, value: String(c.id) })),
            serialize: joinComma,
        });
    }

    if (supplierModel.has_routes && isSupplierUser) {
        classification.push({
            key: "route_id",
            label: "order_route",
            type: "select",
            apiKey: "route_id",
            options: [{ label: formatMessage("all"), value: "" }, ...ctx.orderRoutes.map((r) => ({ label: r.name, value: String(r.id) }))],
        });
    }

    if (supplierModel.has_multi_workflows && isAdmin) {
        classification.push({
            key: "workflow_id",
            label: "workflow",
            type: "select",
            apiKey: "workflow_id",
            options: [{ label: formatMessage("all"), value: "" }, ...ctx.workflows.map((w) => ({ label: w.name, value: String(w.id) }))],
        });
    }

    // ── Section 4: Date Ranges ──
    const dates: IFilterField[] = [
        { key: "created_at_range_start", label: "created_from", type: "date", apiKey: "created_at_range_start" },
        { key: "created_at_range_end", label: "created_to", type: "date", apiKey: "created_at_range_end" },
    ];

    if (isSupplierUser) {
        dates.push({ key: "updated_at", label: "last_updated", type: "date", apiKey: "updated_at" });
    }

    if (supplierModel.has_do_not_ship_before_date && !isQuotes) {
        dates.push({
            key: "do_not_ship_before_start_date",
            label: "dns_before_from",
            type: "date",
            apiKey: "do_not_ship_before_start_date",
        });
        dates.push({ key: "do_not_ship_before_end_date", label: "dns_before_to", type: "date", apiKey: "do_not_ship_before_end_date" });
    }

    if (supplierModel.has_delivery_deadlines && !isQuotes) {
        dates.push({ key: "delivery_date_range_start", label: "delivery_date_from", type: "date", apiKey: "delivery_date_range_start" });
        dates.push({ key: "delivery_date_range_end", label: "delivery_date_to", type: "date", apiKey: "delivery_date_range_end" });
    }

    if (orderDetails.includes("deceased_date") && !isQuotes) {
        dates.push({ key: "deceased_start_date", label: "deceased_date_from", type: "date", apiKey: "deceased_start_date" });
        dates.push({ key: "deceased_end_date", label: "deceased_date_to", type: "date", apiKey: "deceased_end_date" });
    }

    // ── Section 5: Flags ──
    const flags: IFilterField[] = [];

    if (!isPanasonic && isSupplierUser && !isQuotes) {
        flags.push({ key: "filter_notes", label: "has_note", type: "toggle", apiKey: "filter_notes" });
    }

    if (supplierModel.has_serials) {
        flags.push({ key: "has_serial_items", label: "has_serial_items", type: "toggle", apiKey: "has_serial_items", serialize: asOne });
    }

    if (supplierModel.has_local_delivery) {
        flags.push({ key: "is_local_delivery", label: "local_delivery", type: "toggle", apiKey: "is_local_delivery", serialize: asOne });
    }

    if (supplierModel.has_local_pickup) {
        flags.push({ key: "is_local_pickup", label: "local_pickup", type: "toggle", apiKey: "is_local_pickup", serialize: asOne });
    }

    if (!isPanasonic && isSupplierUser) {
        flags.push({ key: "include_sub_accounts", label: "include_sub_accounts", type: "toggle", apiKey: "include_sub_accounts" });
    }

    if (supplierModel.has_backorders && !isQuotes && !isTemplates) {
        flags.push({ key: "filter_only_backorder", label: "is_backorder", type: "toggle", apiKey: "filter_only_backorder" });
    }

    if (supplierModel.has_dropshipping) {
        flags.push({ key: "is_dropship", label: "is_dropship", type: "toggle", apiKey: "is_dropship", serialize: asOne });
    }

    if (supplierModel.has_consignment && !isQuotes) {
        flags.push({ key: "filter_only_consignment", label: "is_consignment", type: "toggle", apiKey: "filter_only_consignment" });
    }

    if (supplierModel.has_remakes && !isQuotes) {
        flags.push({ key: "filter_remakes", label: "remakes", type: "toggle", apiKey: "filter_remakes" });
    }

    if ((supplierModel.has_expedited || supplierModel.has_service_levels) && !isQuotes) {
        flags.push({ key: "filter_expedited", label: "expedited", type: "toggle", apiKey: "filter_expedited" });
    }

    if (isSupplierUser && supplierModel.has_percent_fillable) {
        flags.push({ key: PERCENT_FILLABLE_KEY, label: "percent_fillable", type: "toggle", apiKey: PERCENT_FILLABLE_API_KEY });
    }

    // ── Section 6: Document Status (orders only: the legacy query builder skips every one of these for quotes) ──
    const documents: IFilterField[] = [];

    if (isSupplierUser && !isQuotes && integration?.has_qbo) {
        documents.push({
            key: "qbo_invoice_emailed",
            label: "invoice_emailed",
            type: "select",
            apiKey: "qbo_invoice_emailed",
            options: yesNoOptions,
        });
        documents.push({
            key: "qbo_invoice_created",
            label: "invoice_created",
            type: "select",
            apiKey: "qbo_invoice_created",
            options: yesNoOptions,
        });

        // Orders with a captured payment that should be in QuickBooks and isn't (not sent, failed, or over the order
        // total). Templates take no payments, and with payment sync off nothing is ever sent.
        if (!isTemplates && integration.qbo_payment_sync_mode !== QBOPaymentSyncModeEnum.OFF) {
            documents.push({
                key: "qbo_payment_not_sent",
                label: "payment_not_in_quickbooks",
                type: "toggle",
                apiKey: "qbo_payment_not_sent",
            });
        }
    }

    if (isSupplierUser && !isQuotes && integration?.has_qbd) {
        documents.push({
            key: "qbd_invoice_created",
            label: "invoice_created",
            type: "select",
            apiKey: "qbd_invoice_created",
            options: yesNoOptions,
        });
        documents.push({
            key: "qbd_sales_order_created",
            label: "sales_order_created",
            type: "select",
            apiKey: "qbd_sales_order_created",
            options: yesNoOptions,
        });
    }

    if (supplierModel.has_accounting && !isQuotes) {
        documents.push({ key: "has_accounting", label: "has_accounting", type: "select", apiKey: "has_accounting", options: yesNoOptions });
    }

    if (integration?.has_unanet && !isQuotes) {
        documents.push({
            key: "has_unanet_sync",
            label: "has_unanet_sync",
            type: "select",
            apiKey: "has_unanet_sync",
            options: yesNoOptions,
        });
    }

    // ── Section 7: Filter Out (exclusion) ──
    const filterOut: IFilterField[] = [];

    if (isTemplates) {
        // Templates never move through the order lifecycle: the legacy OrderFilter hides the whole Filter Out group.
    } else if (isQuotes) {
        // Quotes are always preliminary; their only exclusion is the closed-quote toggle (legacy "Filter: Closed").
        filterOut.push({ key: "filter_closed", label: "closed", type: "toggle", apiKey: "filter_closed" });
    } else {
        if (!isPanasonic) {
            filterOut.push({ key: "filter_preliminary", label: "preliminary", type: "toggle", apiKey: "filter_preliminary" });
            filterOut.push({ key: "filter_shipped", label: "shipped", type: "toggle", apiKey: "filter_shipped" });
        }

        if (supplierModel.has_local_pickup) {
            filterOut.push({ key: "filter_picked_up", label: "picked_up", type: "toggle", apiKey: "filter_picked_up" });
        }

        if (supplierModel.has_local_delivery) {
            filterOut.push({ key: "filter_delivered", label: "delivered", type: "toggle", apiKey: "filter_delivered" });
        }

        if (supplierModel.has_remakes && isSupplierUser) {
            filterOut.push({ key: "filter_cancelled", label: "cancelled", type: "toggle", apiKey: "filter_cancelled" });
        }

        filterOut.push({
            key: "filter_older_three_months",
            label: "older_three_months",
            type: "toggle",
            apiKey: "filter_older_three_months",
        });
    }

    const sections: IFilterSection[] = [
        { id: "quick", title: "quick_filters", filters: quick },
        { id: "identification", title: "order_identification", filters: identification },
        { id: "classification", title: "order_classification", filters: classification },
        { id: "dates", title: "date_ranges", filters: dates },
        { id: "flags", title: "flags", filters: flags },
        { id: "documents", title: "document_status", filters: documents },
        { id: "filterOut", title: "filter_out", isExclusion: true, filters: filterOut },
    ];

    return applySupplierSearchConfig(sections, supplierModel.orders_search_config);
}

/** Legacy OrderSearchColEnum → field keys, for suppliers with a custom `orders_search_config`. */
const SEARCH_COL_FIELD_KEYS: { [col in OrderSearchColEnum]?: string[] } = {
    [OrderSearchColEnum.VENDOR]: ["vendor"],
    [OrderSearchColEnum.CLIENT]: ["client"],
    [OrderSearchColEnum.ORDER]: ["order"],
    [OrderSearchColEnum.PO]: ["po_no"],
    [OrderSearchColEnum.DESCRIPTION]: ["description"],
    [OrderSearchColEnum.SHIP_DATE_RANGE]: ["ship_date_range_start", "ship_date_range_end"],
    [OrderSearchColEnum.LAST_UPDATED]: ["updated_at"],
    [OrderSearchColEnum.STATUS]: ["order_state"],
    [OrderSearchColEnum.FILTER_PRELIMINARY]: [
        "filter_preliminary",
        "filter_shipped",
        "filter_picked_up",
        "filter_delivered",
        "filter_cancelled",
    ],
    [OrderSearchColEnum.REMAKE]: ["filter_remakes"],
    [OrderSearchColEnum.EXPEDITED]: ["filter_expedited"],
    [OrderSearchColEnum.PERCENTAGE_FILLABLE]: [PERCENT_FILLABLE_KEY],
    [OrderSearchColEnum.THIRD_PARTY_ORDER_REF]: ["third_party_order_ref"],
    [OrderSearchColEnum.ORDER_CATEGORY]: ["selected_order_categories"],
    [OrderSearchColEnum.ACCOUNTING]: ["has_accounting"],
    [OrderSearchColEnum.QBO_INVOICE_CREATED]: ["qbo_invoice_created"],
    [OrderSearchColEnum.QBO_INVOICE_EMAILED]: ["qbo_invoice_emailed"],
    [OrderSearchColEnum.CATALOG]: ["catalog"],
    [OrderSearchColEnum.OLDER_THREE_MONTHS]: ["filter_older_three_months"],
};

/**
 * When a supplier has a custom search config, keep only the configured fields (in their section order)
 * and apply any title overrides. Without a config the sections are returned unchanged.
 */
function applySupplierSearchConfig(sections: IFilterSection[], config?: IOrderSearchConfig[]): IFilterSection[] {
    if (!config || !config.length) {
        return sections;
    }

    const allowed = new Map<string, string | undefined>();

    for (const item of config) {
        for (const key of SEARCH_COL_FIELD_KEYS[item.searchCol] || []) {
            allowed.set(key, item.titleOverride);
        }
    }

    return sections.map((section) => ({
        ...section,
        filters: section.filters
            .filter((field) => allowed.has(field.key))
            .map((field) => {
                const override = allowed.get(field.key);
                return override ? { ...field, label: override } : field;
            }),
    }));
}
